import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { rangeFromKey, rangeKey, rangeView, sameRange, zoneDayWithin } from './filters-model'
import { UTC } from '../date/local-time'

// The page's time range as every view shows it (owner ruling 2026-10-06).
// A day picked on a calendar (the strip above the timeline, or the
// Insights calendar) becomes the page's range, and the range it replaced
// is kept in the URL (?was=) so the "Showing Oct 6, 2026 ×" chip can go
// back to it. Any other change of the range forgets it.
export const WAS = 'was'

// { range, back, pickDay, clearDay, setFilters }: range is rangeView of
// the page's filters, back the range the chip returns to, and setFilters
// the page's own, forgetting ?was when the range changes any other way.
export const useDayRange = (filters, setFilters) => {
  const location = useLocation()
  const was = new URLSearchParams(location.search).get(WAS)
  const key = rangeKey(filters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const range = useMemo(() => rangeView(filters), [key])
  const set = useCallback((next, opts = {}) => {
    const forget = !sameRange(next, filters) && !(opts.set && WAS in opts.set)
    setFilters(next, forget ? { ...opts, set: { ...(opts.set || {}), [WAS]: null } } : opts)
  }, [filters, setFilters])
  // a cell counts its day (in the calendar's zone, w.timeZone: the
  // reader's; a server that names none counted UTC days) within the
  // calendar's window, so the day is its local
  // midnights and an edge day only the part of it the cell counted; a
  // second day keeps the range the first one replaced
  const pickDay = useCallback((day, w) => set(
    { ...filters, ...zoneDayWithin(day, w && w.from, w && w.to, (w && w.timeZone) || UTC) },
    { push: true, set: { [WAS]: range.day ? was || '' : key } }
  ), [filters, set, range.day, was, key])
  const clearDay = useCallback(() => set({ ...filters, ...rangeFromKey(was) }, { push: true, set: { [WAS]: null } }), [filters, set, was])
  const back = useMemo(() => rangeView(rangeFromKey(was)), [was])
  return { range, back, pickDay, clearDay, setFilters: set }
}

// "Showing Oct 6, 2026 ×" while the range is one day.
export const DayChip = ({ range, back, onClear }) => {
  if (!range || !range.day) return null
  const to = /^Last /.test(back.label) ? back.label.toLowerCase() : back.label
  return (
    <div className="au-daychip" role="status" aria-label="One day shown">
      <span>Showing <b>{range.label}</b></span>
      <button type="button" className="au-daychip-x" aria-label={`Clear the day and go back to ${to}`} title={`Back to ${to}`} onClick={onClear}>×</button>
    </div>
  )
}

// The nearest ancestor that scrolls (the workspace pane), else the page.
export const scrollParent = el => {
  for (let n = el && el.parentElement; n; n = n.parentElement) {
    const { overflowY } = window.getComputedStyle(n)
    if (/(auto|scroll)/.test(overflowY) && n.scrollHeight > n.clientHeight) return n
  }
  return document.scrollingElement || document.documentElement
}

// Keeps the results in view across a filter, range or view change. A
// change swaps the results (a view's first load, a shorter list), so the
// page can shrink under a reader scrolled into them, and the browser then
// clamps the scroll to the new bottom of the page: the exports table. When
// the results' top (ref) is above the visible area, it is brought to the
// top instead, before the browser paints. A reader above the results
// (in the filters) keeps their place. key: what changed.
export const useResultsInView = (ref, key) => {
  const first = useRef(true)
  useLayoutEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    const el = ref.current
    if (!el) return
    const box = scrollParent(el)
    const page = box === document.scrollingElement || box === document.documentElement
    const top = el.getBoundingClientRect().top - (page ? 0 : box.getBoundingClientRect().top)
    if (top < 0) box.scrollTop += top
  }, [ref, key])
}
